import { ActivityInput, ActivityType, Domain } from '../../../graphql/generated/graphql.ts';
import { FC } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Button, Stack, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { ActivityDisplay } from '../../../components/activity-planning/activity-display.tsx';
import { useStudyQuery } from '../../../graphql/generated/operations/study.ts';

export interface TypeSelectionProps {
  input: Partial<ActivityInput>;
  domain: Partial<Domain>;
  onNext: (input: Partial<ActivityInput>) => void;
}

export const TypeSelection: FC<TypeSelectionProps> = ({ domain, input, onNext }) => {
  const { data } = useStudyQuery();
  return (
    <>
      <ActivityDisplay activity={input} domain={domain} />
      {!data?.study.allowSocialActivity && (
        <Typography variant="h6" sx={{ my: 2 }}>
          Great! You chose an activity. Now, tell us if it's a pleasant activity or a challenging/meaningful activity for you.
        </Typography>
      )}
      {data?.study.allowSocialActivity && (
        <Typography variant="h6" sx={{ my: 2 }}>
          Great! You chose an activity. Now, tell us if it's a pleasant activity, a challenging/meaningful activity, or social connection for you.
        </Typography>
      )}
      <Accordion>
        <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} aria-controls="panel1-content" id="panel1-header">
          Pleasant Activity
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <span style={{ fontWeight: 'bold' }}>Pleasant activities</span> involve a short, pleasant break from the pace of life, are enjoyable, and make us feel good. Examples
            are reading, taking a bath, watching a film, or going to a new restaurant.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => {
              onNext({
                ...input,
                type: ActivityType.Pleasant
              });
            }}
          >
            Select Pleasant Activity
          </Button>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} aria-controls="panel1-content" id="panel1-header">
          Challenging / Meaningful Activity
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography>
              <span style={{ fontWeight: 'bold' }}>Challenging activities</span> absorb the mind, require concentration, and promote a sense of accomplishment. Challenging
              activities make you feel as if time is 'flying by.' These activities are opportunities to grow in a new skill or strengthen your current skills. Examples are learning
              a new hobby or skill, building something, or planning an event.
            </Typography>
            <Typography>
              <span style={{ fontWeight: 'bold' }}>Meaningful activities</span> also make you feel good about yourself, but on a more significant level: they give you a sense of
              purpose or self-worth, and you feel like making a difference and that you are acting in line with your values. Examples are: doing volunteer work, attending religious
              services, meditating, or keeping a gratitude journal.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                onNext({
                  ...input,
                  type: ActivityType.Meaningful
                });
              }}
            >
              Select Challenging / Meaningful Activity
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>
      {!!data?.study?.allowSocialActivity && (
        <Accordion>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} aria-controls="panel1-content" id="panel1-header">
            Social Connection
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Typography>
                <span style={{ fontWeight: 'bold' }}>Social Connections Activities</span> involve interactions with others
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  onNext({
                    ...input,
                    type: ActivityType.Social
                  });
                }}
              >
                Select Social Connection Activity
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
